/**
 * Policy Details (Policy group). MVP per Andressa's Figma: a single
 * optional Policy Number field, on its own page so it can grow later
 * without migrating data. Backed by the real `/v2/policy/` API — load
 * the submission's policy on mount, create or update on Save.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Input } from "@tricura/atlas-ui";
import { createPolicy, getPolicyForSubmission, updatePolicy } from "../../../api";
import { FormGroup } from "../../../components/form-group";
import { SubmissionFooter } from "../../../components/submission-footer";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";

const SECTION_ID = "policy-details";

export function PolicyDetailsPage() {
    const { detail } = useSubmissionContext();
    const navigate = useNavigate();
    const [policyNumber, setPolicyNumber] = useState("");
    /** Last-persisted policy number — drives the footer's dirty flag. */
    const [savedNumber, setSavedNumber] = useState("");
    /** Existing policy id (PATCH target) — null until one is created. */
    const [policyId, setPolicyId] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    /** Backend error surfaced under the field (e.g. the final_premium gate
     *  or "a policy already exists"), shown via the same inline input-error
     *  pattern as the rest of the forms. */
    const [saveError, setSaveError] = useState<string | null>(null);

    // Load the submission's policy (if any) on mount.
    useEffect(() => {
        if (!detail.id) return;
        let cancelled = false;
        void getPolicyForSubmission(detail.id)
            .then((p) => {
                if (cancelled || !p) return;
                setPolicyId(p.id);
                setPolicyNumber(p.policy_number ?? "");
                setSavedNumber(p.policy_number ?? "");
            })
            .catch(() => undefined);
        return () => {
            cancelled = true;
        };
    }, [detail.id]);

    const next = getNextSection(SECTION_ID);

    // Save persists the policy number without navigating; Continue advances
    // (the footer guards against leaving while dirty).
    const onSave = useCallback(async () => {
        setIsSaving(true);
        setSaveError(null);
        try {
            const trimmed = policyNumber.trim();
            // Policy Number is optional — only hit the API when there's a
            // value to save or an existing policy to update.
            if (detail.id && (trimmed || policyId)) {
                const saved = policyId ? await updatePolicy(policyId, trimmed) : await createPolicy(detail.id, trimmed);
                setPolicyId(saved.id);
            }
            setSavedNumber(trimmed);
        } catch (err) {
            // Surface the backend's message inline under the field (ApiError
            // carries FastAPI's `detail` — e.g. the final_premium gate or
            // "A policy already exists for this submission.").
            setSaveError(err instanceof Error ? err.message : "Couldn't save the policy. Please try again.");
        } finally {
            setIsSaving(false);
        }
    }, [detail.id, policyNumber, policyId]);
    const onContinue = () => navigate(next ? `../${next.path}` : "/submissions");
    const isDirty = policyNumber.trim() !== savedNumber;

    return (
        <>
            <div className="w-full space-y-6">
                <header className="flex flex-col gap-1" data-autotest="policy-details-heading">
                    <h2 className="text-primary text-md font-semibold">Policy Details</h2>
                    <p className="text-tertiary text-sm">Core policy identifiers and account information</p>
                </header>

                <FormGroup title="Policy Setup" data-autotest="policy-details-policy-setup-card">
                    <div className="flex max-w-md flex-col gap-1.5">
                        <span className="text-secondary text-sm font-medium">Policy Number</span>
                        <Input
                            size="sm"
                            aria-label="Policy Number"
                            placeholder="Enter Policy Number"
                            value={policyNumber}
                            onChange={(v) => {
                                setPolicyNumber(v);
                                if (saveError) setSaveError(null);
                            }}
                            isInvalid={!!saveError}
                            data-autotest="policy-details-policy-number-input"
                        />
                        {/* Render the error in-flow (not the Input's absolute hint):
                         * a multi-line backend message — e.g. the final_premium
                         * gate — would otherwise be clipped by the card's
                         * overflow-hidden. In-flow lets the card grow. */}
                        {saveError && (
                            <p
                                role="alert"
                                className="text-error-primary text-xs leading-tight"
                                data-autotest="policy-details-policy-number-error"
                            >
                                {saveError}
                            </p>
                        )}
                    </div>
                </FormGroup>
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </>
    );
}
